import React, { useState, useMemo, useEffect } from 'react';
import DialogForm from 'components/DialogForm';
import ResultadosForm from 'forms/ResultadosForm';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import PrintIcon from '@material-ui/icons/Print';
import PersonIcon from '@material-ui/icons/Person';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import { toast } from 'react-toastify';
import { format } from 'utils/form';
import api from 'services/api';
import { getFormattedDate } from 'utils/transform';
import DataTable from 'components/DataTable';
import useTable from 'hooks/useTable';

export default function SegundaViaReciboDialog({
  onClose = () => {},
  open = false,
}) {
  const [associado, setAssociado] = useState({});
  const [loadingDownload, setLoadingDownload] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(null);
  const [loadingBulk, setLoadingBulk] = useState(false);
  const cpfMask = format('XXX.XXX.XXX-XX');

  const formattedTelefones = useMemo(() => {
    if (!associado?.telefones || associado.telefones.length === 0) {
      return 'Não informado';
    }
    return associado.telefones.map(t => t.telefone).join(', ');
  }, [associado]);

  const [loadingSearch, setLoadingSearch] = useState(false);

  const { onRefresh, onClear, data, loading, ...dataTableProps } = useTable(
    'inscricoes',
    {
      download: false,
      viewColumns: false,
      search: false,
      print: false,
      selectableRows: 'multiple',
      params: {
        sort: 'created_at',
        order: 'desc',
      },
    },
    false,
  );

  // Clear associate info and table data when closing or opening the modal
  useEffect(() => {
    setAssociado({});
    setLoadingSearch(false);
    onClear();
  }, [open]);

  // Turn off loadingSearch once the table finishes loading
  useEffect(() => {
    if (!loading) {
      setLoadingSearch(false);
    }
  }, [loading]);

  const onDownloadRecibo = async numeroSorteio => {
    if (!numeroSorteio) return;
    setLoadingDownload(numeroSorteio);
    try {
      const { data: reciboData } = await api.get(
        `comprovante-inscricao/${numeroSorteio}`,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/pdf',
          },
        },
      );

      const file = new Blob([reciboData], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = `comprovante-${numeroSorteio}.pdf`;
      link.click();

      toast.success('Recibo baixado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao baixar o recibo.');
    } finally {
      setLoadingDownload(null);
    }
  };

  const onPrintRecibo = async numeroSorteio => {
    if (!numeroSorteio) return;
    setLoadingPrint(numeroSorteio);
    try {
      const { data: reciboData } = await api.get(
        `comprovante-inscricao/${numeroSorteio}`,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/pdf',
          },
        },
      );

      const file = new Blob([reciboData], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');

      toast.success('Recibo aberto para impressão!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar recibo para impressão.');
    } finally {
      setLoadingPrint(null);
    }
  };

  const onBulkDownload = async selectedRows => {
    const codigos = selectedRows
      .map(idx => data[idx]?.numero_sorteio)
      .filter(Boolean);

    if (codigos.length === 0) {
      toast.warn('Nenhuma inscrição selecionada para download.');
      return;
    }

    setLoadingBulk(true);
    try {
      const { data: reciboData } = await api.get('comprovante-inscricao-bulk', {
        params: { codigos: codigos.join(',') },
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/pdf',
        },
      });

      const file = new Blob([reciboData], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = `comprovantes-em-lote-${codigos.length}.pdf`;
      link.click();

      toast.success(
        `${codigos.length} recibo(s) baixado(s) em um único PDF com sucesso!`,
      );
    } catch (error) {
      console.error(error);
      toast.error('Erro ao baixar os recibos em lote.');
    } finally {
      setLoadingBulk(false);
    }
  };

  const customToolbarSelect = (
    selectedRowsData,
    _displayData,
    setSelectedRows,
  ) => {
    const selectedIndexes = selectedRowsData.data.map(r => r.dataIndex);
    return (
      <Box display="flex" alignItems="center" style={{ paddingRight: 16 }}>
        <Tooltip
          title={`Download em Massa (${selectedIndexes.length} selecionado(s))`}
        >
          <IconButton
            color="inherit"
            disabled={loadingBulk}
            onClick={async () => {
              await onBulkDownload(selectedIndexes);
              setSelectedRows([]);
            }}
          >
            {loadingBulk ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <GetAppIcon />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const columns = useMemo(
    () => [
      {
        name: 'numero_sorteio',
        label: '#',
        options: {
          customBodyRender(numero) {
            return numero ? numero : '-';
          },
        },
      },
      {
        name: 'sorteio',
        label: 'Campanha',
        options: {
          customBodyRender: sorteio => sorteio?.campanha?.titulo || '-',
        },
      },
      {
        name: 'sorteio',
        label: 'Sorteio',
        options: {
          customBodyRender: sorteio => sorteio?.nome || '-',
        },
      },
      {
        name: 'periodo',
        label: 'Período',
        options: {
          customBodyRender: periodo => {
            if (!periodo) return '-';
            const dataInicio = getFormattedDate(periodo.data_inicio);
            const dataFinal = getFormattedDate(periodo.data_fim);
            return `${periodo?.nome} - ${dataInicio} até ${dataFinal}`;
          },
        },
      },
      {
        name: 'created_at',
        label: 'Data da Inscrição',
        options: {
          customBodyRender: date => getFormattedDate(date, 'Pp'),
        },
      },
      {
        name: 'acoes',
        label: 'Ações',
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: dataIndex => {
            const row = data[dataIndex];
            const numeroSorteio = row?.numero_sorteio;
            return (
              <Box display="flex" style={{ gap: 8 }}>
                <Tooltip title="Download do Recibo">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onDownloadRecibo(numeroSorteio)}
                    disabled={
                      !numeroSorteio || loadingDownload === numeroSorteio
                    }
                  >
                    {loadingDownload === numeroSorteio ? (
                      <CircularProgress size={20} />
                    ) : (
                      <GetAppIcon />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Imprimir Recibo">
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => onPrintRecibo(numeroSorteio)}
                    disabled={!numeroSorteio || loadingPrint === numeroSorteio}
                  >
                    {loadingPrint === numeroSorteio ? (
                      <CircularProgress size={20} color="secondary" />
                    ) : (
                      <PrintIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
        },
      },
    ],
    [data, associado, loadingDownload, loadingPrint],
  );

  async function onSubmit(values) {
    const params = { cpf: values.associado_cpf, include: 'telefones' };

    setLoadingSearch(true);
    setAssociado({});

    try {
      const { data: responseData } = await api.get('associados', { params });

      if (!responseData.data[0]?.id) {
        toast.warn(
          `Nenhum recibo encontrado para o CPF informado. ${values.associado_cpf}`,
        );
        setLoadingSearch(false);
        return;
      }

      const associadoEncontrado = responseData.data[0];
      setAssociado(associadoEncontrado);

      onRefresh({
        associado_cpf: associadoEncontrado.cpf,
        sort: 'created_at',
        order: 'desc',
      });
    } catch (error) {
      console.error(error);
      toast.error('Erro ao buscar associado.');
      setLoadingSearch(false);
    }
  }

  return (
    <DialogForm
      title="2ª via recibo"
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
        setAssociado({});
      }}
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="md"
    >
      {({ onClose: dialogClose }) => (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ResultadosForm
              onSubmit={onSubmit}
              footer={({ submitting }) => (
                <DialogActions style={{ paddingLeft: 0 }}>
                  <Button onClick={dialogClose} color="primary">
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    disabled={submitting}
                    type="submit"
                    color="primary"
                  >
                    Pesquisar
                  </Button>
                </DialogActions>
              )}
            />
          </Grid>
          {associado.id && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                style={{ padding: 24, borderRadius: 12 }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar
                      style={{
                        width: 64,
                        height: 64,
                        backgroundColor: '#1976d2',
                      }}
                    >
                      <PersonIcon fontSize="large" />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 'bold', color: '#333' }}
                    >
                      {associado.nome}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 4,
                      }}
                    >
                      <EmailIcon fontSize="small" style={{ marginRight: 8 }} />
                      {associado.email || 'Email não informado'}
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={3} mb={2}>
                  <Divider />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <AssignmentIndIcon
                        fontSize="small"
                        style={{ marginRight: 8 }}
                      />{' '}
                      CPF
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {associado.cpf
                        ? cpfMask.format(associado.cpf)
                        : 'Não informado'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <AssignmentIndIcon
                        fontSize="small"
                        style={{ marginRight: 8 }}
                      />{' '}
                      RG
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {associado.rg || 'Não informado'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <PhoneIcon fontSize="small" style={{ marginRight: 8 }} />{' '}
                      Telefone
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {formattedTelefones}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12}>
            <DataTable
              columns={columns}
              data={data}
              loading={loadingSearch || loading}
              {...dataTableProps}
              options={{
                ...dataTableProps.options,
                selectableRows: 'multiple',
                customToolbarSelect,
              }}
            />
          </Grid>
        </Grid>
      )}
    </DialogForm>
  );
}
